import { Col, Pagination, Row, Tabs } from 'antd'
import AppLayout from 'components/layout/layout'
import { useState, useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import PageHeaderNews from './Headers/PageHeaderNews'
import NewsModal from 'pages/Modals/NewsModal'
import PageHeaderBlog from './Headers/PageHeaderBlog'
import ActionHeader from './Headers/ActionHeader'
import BasicNewsLayout from './BasicNewsLayout'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { getNews } from 'api/News'
import Spinner from 'components/Spinner'
import usePagination from 'helper/usePagination'
import Loader from 'helper/Loader'
import BlogActionHeader from './Headers/BlogActionHeader'
import NoBlogPosts from './Components/NoBlogPosts'
import BlogPostsTable from './Components/BlogPostsTable'
import BlogCardView from './Components/BlogCardView'
import BlogPreview from './Components/BlogPreview'
import CreateBlogPost from './Components/CreateBlogPost'
import { getBlogs } from 'api/Blogs/Blogs'
import { useUpdateBlog } from 'api/Blogs/Blogs'
import { debounce } from 'lodash'
import { useBlogPostsViewStore } from 'state/blog/blogPostsView'
import { NewsStatus } from 'types/News'
import { Visibility } from 'types/blogs/blogs'


export const News = () => {
  const organizationData = useSelector((state: any) => state.organization?.organizationData || [])
  const { view } = useBlogPostsViewStore()
  const [isNewsModalVisible, setIsNewsModalVisible] = useState(false)
  const [isNewsEditMode, setIsNewsEditMode] = useState(false)
  const [editingNewsData, setEditingNewsData] = useState(null)
  const {
    data: newsData,
    isLoading: newsLoading,
    isFetching: newsFetching,
    refetch: newsRefetch,
  } = useQuery<any>({
    queryKey: ['all-news'],
    queryFn: () => getNews(params),
    enabled: false,
  })

  const [editingBlogId, setEditingBlogId] = useState<string | null>(null)
  const [previewBlog, setPreviewBlog] = useState<any>(null)
  const [tabLoading, setTabLoading] = useState(false);
  const queryClient = useQueryClient()
  const { mutate: updateBlogPost, isLoading: updatingBlog } = useUpdateBlog()
  const [filterLoading, setFilterLoading] = useState(false)
  const { data: blogsData, isLoading: blogsLoading, isFetching: blogFetching, refetch } = useQuery({
    queryKey: ['all-blogs'],
    queryFn: () => getBlogs(blogParams),
    enabled: false,
  })

  const [blogParams, setBlogParams] = useState<any>({
    page: 1,
    limit: 10,
    visibility: [Visibility.PUBLIC, Visibility.DRAFT],
  })
  const [activeTab, setActiveTab] = useState('1')
  const [params, setParams] = useState<any>({
    page: 1,
    limit: 10,
    status: [NewsStatus.Published],
  })

  const handleNewBlogPost = () => {
    setEditingBlogId(null)
    setPreviewBlog(null) // Clear preview data when starting new blog
    setShowCreateBlog(true)
  }

  const [showCreateBlog, setShowCreateBlog] = useState(false)
  useEffect(() => {
    newsRefetch()
  }, [params])

  // useEffect(() => {
  //   refetch()
  // }, [blogParams])

  const { pinned, news } = newsData || {}

  const { count, totalPages, hasPrevPage, hasNextPage, records } = news || {}
  const handleSearch = (query: string) => {
    setParams({
      ...params,
      search: query,
    })
  }

  const debouncedBlogSearch = useMemo(
    () => debounce((query: string) => {
      if (query.trim()) {
        setBlogParams(prev => ({
          ...prev,
          search: query,
        }))
      } else {
        setBlogParams(prev => {
          const { search, ...rest } = prev
          return rest
        })
      }
    }, 1000),
    []
  )

  const handleTabChange = (key: string) => {
    setActiveTab(key);

    if (key === '2') { // Blog tab
      setTabLoading(true);
      refetch().finally(() => setTabLoading(false));
    }
  };

  useEffect(() => {
    setFilterLoading(true)
    refetch().finally(() => setFilterLoading(false))
  }, [blogParams])

  const handleBlogSearch = (query: string) => {
    debouncedBlogSearch(query)
  }
  const [paginationInfo, onTableChange, PaginationNoTabel] = usePagination({
    totalCount: news?.count || 0,
    currentPage: params.page,
    setParams: setParams,
  })
  const [blogPaginationInfo, , BlogPaginationNoTabel] = usePagination({
    totalCount: blogsData?.count || 0,
    currentPage: blogParams.page,
    setParams: setBlogParams,
  })

  const handleCreateBlogSuccess = () => {
    setShowCreateBlog(false)
    setEditingBlogId(null)
    setPreviewBlog(null) // Clear preview data when blog is created/updated
    // Force refetch to get latest data
    setTimeout(() => {
      refetch()
    }, 100)
  }

  const handleEditBlog = (blogId: string) => {
    setEditingBlogId(blogId)
    setPreviewBlog(null) // Clear preview data when starting edit
    // Ensure clean state when switching to edit mode
    setTimeout(() => {
      setShowCreateBlog(true)
    }, 50)
  }

  const handlePostNews = () => {
    setIsNewsEditMode(false)
    setEditingNewsData(null)
    setIsNewsModalVisible(true)
  }

  const handleEditNews = (newsData: any) => {
    setIsNewsEditMode(true)
    setEditingNewsData(newsData)
    setIsNewsModalVisible(true)
  }

  const handlePreviewBlog = (blog: any) => {
    const transformedBlog = {
      ...blog,
      author: blog.authors?.map((a: any) => `${a.firstName} ${a.lastName}`).join(', '),
      authorAvatar: blog.authors?.length > 0 ? blog.authors[0].avatar : null,
      featuredImage: blog.featureImage || null,
      publishDate: blog.createdAt,
      // readTime: blog.readTime || Math.ceil((blog.content || '').split(' ').length / 200) || 5,
      sharingOptions: blog.sharingOptions || {}
    }
    setPreviewBlog(transformedBlog)
  }

  const handleSaveFromPreview = () => {
    if (!previewBlog) return

    const blogData = {
      title: previewBlog.title,
      excerpt: previewBlog.excerpt,
      content: previewBlog.content,
      visibility: previewBlog.visibility,
      featureImageId: previewBlog.featureImage?.id,
      authorIds: previewBlog.authors?.map((a: any) => a.id) || [],
      organizationId: previewBlog.organizationId,
      // readingTime: previewBlog.readTime,
      sharingOptions: previewBlog.sharingOptions
    }

    updateBlogPost({
      blogId: previewBlog._id,
      payload: blogData
    }, {
      onSuccess: () => {
        queryClient.invalidateQueries(['all-blogs'])
        refetch()
        setPreviewBlog(null)
      },
      onError: () => {
        console.error('Failed to save blog')
      }
    })
  }


  const setNewsModalVisible = (visible: boolean) => {
    setIsNewsModalVisible(visible)
    if (!visible) {
      setIsNewsEditMode(false)
      setEditingNewsData(null)
    }
  }

  return (
    <AppLayout>
      {showCreateBlog ? (
        <CreateBlogPost
          // key={editingBlogId || 'create-new'}
          onSuccess={handleCreateBlogSuccess}
          blogId={editingBlogId || undefined}
          isEditMode={!!editingBlogId}
        />
      ) : previewBlog ? (
        <BlogPreview
          visible={true}
          formData={previewBlog}
          isSaving={updatingBlog}
          onEdit={() => {
            setPreviewBlog(null)
            handleEditBlog(previewBlog._id)
          }}
          onSave={handleSaveFromPreview}
          onBackToBlog={() => setPreviewBlog(null)}
        />
      ) : (
        <>
          {activeTab === '1' ? (
            <PageHeaderNews data={news} refetch={newsRefetch} onPostNews={handlePostNews} />
          ) : (
            <PageHeaderBlog data={news} onNewBlogPost={handleNewBlogPost}></PageHeaderBlog>
          )}
          <Tabs
            activeKey={activeTab}
            onChange={handleTabChange}
            items={[
              {
                key: '1',
                label: 'News',
                children: (
                  <>
                    <NewsModal
                      isModalVisible={isNewsModalVisible}
                      setModalVisible={setNewsModalVisible}
                      organizationData={organizationData ?? []}
                      currentData={editingNewsData}
                      onNewsPosted={newsRefetch}
                      isEditMode={isNewsEditMode}
                    />
                    <Row>
                      <Col>
                        <ActionHeader
                          search={handleSearch}
                          parms={params}
                          setParms={setParams}
                          isLoading={newsLoading || newsFetching}
                        ></ActionHeader>
                      </Col>
                      <Col>
                        {newsLoading || newsFetching ? (
                          <div
                            className="flex justify-center align-center items-center"
                            style={{ height: '80vh' }}
                          >
                            <Loader />
                          </div>
                        ) : (
                          records?.map((item, index) => (
                            <BasicNewsLayout
                              key={index}
                              newsData={item}
                              parms={params}
                              onRefresh={newsRefetch}
                              onEditNews={handleEditNews}
                            />
                          ))
                        )}
                      </Col>
                      <Col className="justify-center flex mr-20px ml-20px ">
                        <Pagination
                          className="pb-20"
                          style={{ width: '100%', display: 'flex' }}
                          {...paginationInfo}
                          onChange={PaginationNoTabel}
                        />
                      </Col>
                    </Row>
                  </>
                ),
              },
              {
                key: '2',
                label: 'Blog',
                children: (
                  <div>
                    <BlogActionHeader
                      search={handleBlogSearch}
                      parms={blogParams}
                      setParms={(newParams) => {
                        setFilterLoading(true)   // start loader
                        setBlogParams(newParams)
                      }}
                      isLoading={blogFetching}
                      onNewBlogPost={handleNewBlogPost}
                    ></BlogActionHeader>
                    {activeTab === '2' && (
                      tabLoading || blogsLoading || filterLoading ? (
                        <div style={{ height: '80vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                          <Loader />
                        </div>
                      ) : (
                        <>
                          {blogsData?.records?.length > 0
                            ? view === 'card'
                              ? <BlogCardView blogs={blogsData} blogsLoading={blogsLoading} onEdit={handleEditBlog} onPreview={handlePreviewBlog} refetch={refetch} />
                              : <BlogPostsTable blogs={blogsData} blogsLoading={blogsLoading} onEdit={handleEditBlog} refetch={refetch} params={blogParams} setParams={setBlogParams} />
                            : <NoBlogPosts onNewBlogPost={handleNewBlogPost} />
                          }
                          {(blogsData?.hasNextPage || blogsData?.hasPrevPage) && (
                            <div className="justify-center flex mr-20px ml-20px mt-20px">
                              <Pagination
                                className="pb-20"
                                style={{ width: '100%', display: 'flex', justifyContent: 'center' }}
                                responsive={true}
                                showSizeChanger={false}
                                current={blogParams.page}
                                pageSize={blogParams.limit || 10}
                                total={blogsData?.count || 0}
                                onChange={BlogPaginationNoTabel}
                              />
                            </div>
                          )}
                        </>
                      )
                    )}
                  </div>
                ),
              },
            ]}
          />
        </>
      )}
    </AppLayout>
  )
}
export default News
